import { useEffect, useMemo, useState } from 'react';
import { LocateFixed, X } from 'lucide-react';
import MapSelector from './MapSelector';

const toCoordinate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeCoordinates = (coordinates) => {
  const lat = toCoordinate(coordinates?.lat ?? coordinates?.latitud);
  const lng = toCoordinate(coordinates?.lng ?? coordinates?.longitud);
  return lat !== null && lng !== null ? { lat, lng } : null;
};

const isSecureGeolocationOrigin = () => {
  return window.location.protocol === 'https:';
};

const getGeolocationErrorMessage = (error) => {
  if (!error) {
    return 'No se pudo obtener la ubicación actual.';
  }

  if (error.code === error.PERMISSION_DENIED) {
    return 'Permiso de ubicación denegado. Puedes seleccionar la ubicación manualmente en el mapa.';
  }

  if (error.code === error.POSITION_UNAVAILABLE) {
    return 'No se pudo obtener la ubicación actual. Verifica el GPS o la conexión y selecciona manualmente si es necesario.';
  }

  if (error.code === error.TIMEOUT) {
    return 'Tiempo de espera agotado al obtener la ubicación. Intenta nuevamente o selecciona manualmente.';
  }

  return 'No se pudo obtener la ubicación actual. Selecciona manualmente en el mapa.';
};

const LocationPickerModal = ({ isOpen, initialCoordinates, onCancel, onConfirm }) => {
  const normalizedInitialCoordinates = useMemo(
    () => normalizeCoordinates(initialCoordinates),
    [initialCoordinates]
  );
  const [selectedCoordinates, setSelectedCoordinates] = useState(normalizedInitialCoordinates);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedCoordinates(normalizedInitialCoordinates);
      setLoadingLocation(false);
      setLocationError('');
    }
  }, [isOpen, normalizedInitialCoordinates]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedCoordinates) return;
    onConfirm(selectedCoordinates);
  };

  const handleGetCurrentLocation = () => {
    setLocationError('');

    if (!('geolocation' in navigator)) {
      setLocationError('Tu navegador no soporta geolocalización. Puedes seleccionar la ubicación manualmente en el mapa.');
      return;
    }

    if (!isSecureGeolocationOrigin()) {
      setLocationError('La geolocalización requiere conexión segura. Puedes seleccionar la ubicación manualmente en el mapa.');
      return;
    }

    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setSelectedCoordinates({
          lat: coords.latitude,
          lng: coords.longitude,
        });
        setLoadingLocation(false);
      },
      (error) => {
        setLocationError(getGeolocationErrorMessage(error));
        setLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h3 className="text-base font-semibold uppercase tracking-[0.08em] text-slate-800">
            Ubicación en mapa
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleGetCurrentLocation}
              disabled={loadingLocation}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <LocateFixed size={16} />
              {loadingLocation ? 'Obteniendo ubicación...' : 'Obtener mi ubicación'}
            </button>
            <p className="text-xs text-slate-500">
              La ubicación detectada se guarda solo al confirmar.
            </p>
          </div>

          {locationError && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
              {locationError}
            </div>
          )}

          <MapSelector
            coordinates={selectedCoordinates}
            onChange={setSelectedCoordinates}
            isVisible={isOpen}
          />
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg bg-slate-200 px-5 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-300"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedCoordinates}
            className="rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Confirmar ubicación
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;
